import React, { useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  ViewStyle,
} from 'react-native';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { ResidentMobileHeaderWithMenu } from './ResidentMobileHeaderWithMenu';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { RESIDENT_NAV_COLORS } from '@/constants/residents/residentNavigation';

interface BaseResidentMobileScreenProps {
  /** The current screen name for navigation highlighting */
  screenName?: string;
  /** Navigation prop from React Navigation (optional - will use hook if not provided) */
  navigation?: NavigationProp<RootStackParamList>;
  /** Custom content to render in the main area */
  children?: React.ReactNode;
  /** Additional styles for the content container */
  contentContainerStyle?: ViewStyle;
  /** Whether scrolling is enabled (default: false) */
  scrollEnabled?: boolean;
  /** Custom logo press handler */
  onLogoPress?: () => void;
  /** Custom sign out handler (optional - uses default if not provided) */
  onSignOut?: () => void;
  /** Custom notification press handler */
  onNotificationPress?: () => void;
  /** Whether to show notification button */
  showNotification?: boolean;
  /** Use dark background matching Figma design */
  darkBackground?: boolean;
}

/**
 * BaseResidentMobileScreen - Reusable base component for resident mobile screens
 * 
 * This component provides:
 * - Consistent layout and styling with new Figma-based header
 * - Navigation handling via slide-down menu
 * - Sign out functionality
 * - Notification access
 */
export const BaseResidentMobileScreen: React.FC<BaseResidentMobileScreenProps> = React.memo(({
  screenName,
  navigation: propNavigation,
  children,
  contentContainerStyle,
  scrollEnabled = false,
  onLogoPress,
  onSignOut,
  onNotificationPress,
  showNotification = true,
  darkBackground = true,
}) => {
  const hookNavigation = useNavigation<NavigationProp<RootStackParamList>>();
  const navigation = propNavigation || hookNavigation;

  // Use custom hook for resident screen logic
  const {
    userState,
    screenMetadata,
    handleNavigate,
    handleLogoPress: defaultHandleLogoPress,
    handleSignOut: defaultHandleSignOut,
    handleNotificationPress: defaultHandleNotificationPress,
  } = useResidentScreen(screenName, navigation as any);

  // Override handlers if provided
  const finalLogoPress = useCallback(() => {
    if (onLogoPress) {
      onLogoPress();
    } else {
      defaultHandleLogoPress();
    }
  }, [onLogoPress, defaultHandleLogoPress]);

  const finalSignOut = useCallback(async () => {
    if (onSignOut) {
      await onSignOut();
    } else {
      await defaultHandleSignOut();
    }
  }, [onSignOut, defaultHandleSignOut]);

  const finalNotificationPress = useCallback(() => {
    if (onNotificationPress) {
      onNotificationPress();
    } else {
      defaultHandleNotificationPress();
    }
  }, [onNotificationPress, defaultHandleNotificationPress]);

  const containerStyle = darkBackground ? styles.containerDark : styles.container;
  const contentStyle = darkBackground ? styles.contentDark : styles.content;

  return (
    <View style={containerStyle}>
      <ResidentMobileHeaderWithMenu 
        onLogoPress={finalLogoPress}
        onSignOut={finalSignOut}
        onNavigate={handleNavigate}
        onNotificationPress={finalNotificationPress}
        currentPath={screenMetadata.path}
        currentTitle={screenMetadata.title}
        showNotification={showNotification}
      />
    
      {scrollEnabled ? (
        <ScrollView 
          style={contentStyle}
          contentContainerStyle={[styles.contentContainer, contentContainerStyle]}
          showsVerticalScrollIndicator={false}
          scrollEnabled={scrollEnabled}
        >
          {children}
        </ScrollView>
      ) : (
        <View style={[contentStyle, styles.contentContainer, contentContainerStyle]}>
          {children}
        </View>
      )}
    </View>
  );
});

BaseResidentMobileScreen.displayName = 'BaseResidentMobileScreen';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENT_NAV_COLORS.BACKGROUND, // Light background for resident screens
  },
  containerDark: {
    flex: 1,
    backgroundColor: RESIDENT_NAV_COLORS.BACKGROUND,
  },
  content: {
    flex: 1,
  },
  contentDark: {
    flex: 1,
    backgroundColor: RESIDENT_NAV_COLORS.BACKGROUND, // #242424 - matches nav header background
  },
  contentContainer: {
    flexGrow: 1,
  },
});

